import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ClipboardCheck, Loader2, Users } from "lucide-react";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import { toast } from "react-toastify";
import PageHeader from "../../../components/PageHeader";
import { SELECT_STYLES } from "../../../components/DateFilters/constants";
import { useAuth } from "../../../context/AuthContext";
import { canVerTodasLojasUser, hasFullAccessUser } from "../../../lib/screenAccess";
import {
  controleProducaoService,
  RelatorioEquipeResponse,
  RelatorioEquipeUsuario,
} from "../../../services/controleProducaoService";
import { lojaService } from "../../../services/lojaService";
import { Loja } from "../../../types/loja";

function LinhaUsuario({
  usuario,
  mostrarConferente = true,
}: {
  usuario: RelatorioEquipeUsuario;
  mostrarConferente?: boolean;
}) {
  return (
    <tr className={`border-t border-neutral-200 ${usuario.ativo ? "" : "opacity-60"}`}>
      <td className="px-3 py-2 font-medium break-words">{usuario.nome}</td>
      <td className="hidden px-3 py-2 text-neutral-600 md:table-cell">
        {usuario.email}
      </td>
      <td className="px-3 py-2 text-neutral-600">{usuario.roleNome || "—"}</td>
      <td className="px-3 py-2">
        {usuario.ativo ? (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
            Ativo
          </span>
        ) : (
          <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-[11px] font-semibold text-neutral-600">
            Inativo
          </span>
        )}
      </td>
      {mostrarConferente && (
        <td className="hidden px-3 py-2 sm:table-cell">
          {usuario.isConferenteBase ? (
            <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-800">
              Também conferente
            </span>
          ) : (
            <span className="text-neutral-400">—</span>
          )}
        </td>
      )}
    </tr>
  );
}

function TabelaUsuarios({
  usuarios,
  empty,
  mostrarConferente = true,
}: {
  usuarios: RelatorioEquipeUsuario[];
  empty: string;
  mostrarConferente?: boolean;
}) {
  return (
    <table className="w-full table-fixed text-sm">
      <thead className="bg-neutral-50">
        <tr>
          <th className="px-3 py-2 text-left font-semibold text-neutral-600">
            Nome
          </th>
          <th className="hidden px-3 py-2 text-left font-semibold text-neutral-600 md:table-cell">
            E-mail
          </th>
          <th className="px-3 py-2 text-left font-semibold text-neutral-600">
            Cargo
          </th>
          <th className="px-3 py-2 text-left font-semibold text-neutral-600">
            Status
          </th>
          {mostrarConferente && (
            <th className="hidden px-3 py-2 text-left font-semibold text-neutral-600 sm:table-cell">
              Conferente
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {usuarios.length === 0 ? (
          <tr>
            <td
              colSpan={mostrarConferente ? 5 : 4}
              className="px-3 py-6 text-center text-neutral-500"
            >
              {empty}
            </td>
          </tr>
        ) : (
          usuarios.map((usuario) => (
            <LinhaUsuario
              key={usuario.id}
              usuario={usuario}
              mostrarConferente={mostrarConferente}
            />
          ))
        )}
      </tbody>
    </table>
  );
}

type LojaOpt = { value: number; label: string };

const LOJA_SELECT_STYLES = {
  ...(SELECT_STYLES as StylesConfig<LojaOpt, false>),
  control: (base, state) => ({
    ...(SELECT_STYLES.control?.(base, state) ?? base),
    minHeight: "38px",
    height: "38px",
  }),
  valueContainer: (base) => ({
    ...base,
    height: "36px",
    padding: "0 8px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "36px",
  }),
} satisfies StylesConfig<LojaOpt, false>;

const ControleProducaoRelatorioEquipePage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = hasFullAccessUser(user);
  const podeVerTodasLojas = canVerTodasLojasUser(user);
  const [lojas, setLojas] = useState<Loja[]>([]);
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    user?.lojaId
  );
  const [loading, setLoading] = useState(false);
  const [relatorio, setRelatorio] = useState<RelatorioEquipeResponse | null>(
    null
  );

  const lojaOptions = useMemo(
    () =>
      podeVerTodasLojas ? lojas : lojas.filter((l) => l.id === user?.lojaId),
    [podeVerTodasLojas, lojas, user?.lojaId]
  );

  const lojaSelectOptions = useMemo<LojaOpt[]>(
    () => lojaOptions.map((loja) => ({ value: loja.id, label: loja.nome })),
    [lojaOptions]
  );

  const selectedLojaOption =
    lojaSelectOptions.find((o) => o.value === selectedLojaId) ?? null;

  useEffect(() => {
    void lojaService
      .getLojas()
      .then(setLojas)
      .catch(() => setLojas([]));
  }, []);

  useEffect(() => {
    if (selectedLojaId != null) return;
    if (user?.lojaId != null && user.lojaId >= 1) {
      setSelectedLojaId(user.lojaId);
    }
  }, [user?.lojaId, selectedLojaId]);

  const carregar = useCallback(async () => {
    if (!selectedLojaId) return;
    setLoading(true);
    try {
      const data = await controleProducaoService.getRelatorioEquipe({
        lojaId: selectedLojaId,
      });
      setRelatorio(data);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Erro ao gerar relatório.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [selectedLojaId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const resumo = relatorio?.resumo;

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Equipe de Produção"
        description="Por loja: quem produz em cada setor, separado, e quem é conferente da unidade."
      />

      <div className="mt-6 flex flex-col gap-3 rounded-lg border border-neutral-200 bg-white p-4 sm:flex-row sm:items-end sm:gap-3">
        <div className="flex min-w-0 flex-col gap-1.5 sm:w-60">
          <label
            htmlFor="relatorio-equipe-loja"
            className="text-sm font-medium leading-tight text-neutral-700"
          >
            Loja
          </label>
          <ReactSelect<LojaOpt, false>
            inputId="relatorio-equipe-loja"
            value={selectedLojaOption}
            onChange={(option: SingleValue<LojaOpt>) =>
              setSelectedLojaId(option?.value)
            }
            options={lojaSelectOptions}
            isClearable={isAdmin}
            placeholder="Selecionar loja"
            styles={LOJA_SELECT_STYLES}
          />
        </div>
        <button
          type="button"
          onClick={() => void carregar()}
          disabled={loading || !selectedLojaId}
          className="h-[38px] w-full shrink-0 rounded-md bg-primary px-4 text-sm font-semibold text-white disabled:opacity-60 sm:w-auto sm:min-w-[9rem]"
        >
          {loading ? "Gerando..." : "Atualizar"}
        </button>
      </div>
      <p className="mt-2 text-xs text-neutral-500">
        Lista o cadastro atual da loja. Quem está em mais de um setor aparece em
        cada um. Conferentes ficam no bloco próprio, mesmo que também produzam.
      </p>

      {loading && (
        <p className="mt-4 flex items-center gap-2 text-sm text-neutral-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Carregando equipe...
        </p>
      )}

      {relatorio && !loading && resumo && (
        <div className="mt-6 space-y-6">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-neutral-200 bg-white p-4">
              <p className="text-xs font-medium uppercase text-neutral-500">
                Loja
              </p>
              <p className="mt-1 text-xl font-bold text-neutral-900">
                {relatorio.lojaNome}
              </p>
              <p className="mt-0.5 text-xs text-neutral-500">
                {resumo.setoresAtivos} setores ativos
              </p>
            </div>
            <div className="rounded-lg border border-neutral-200 bg-white p-4">
              <p className="flex items-center gap-1.5 text-xs font-medium uppercase text-neutral-500">
                <Users className="h-3.5 w-3.5" />
                Produção
              </p>
              <p className="mt-1 text-2xl font-bold text-neutral-900">
                {resumo.usuariosProducaoDistintos}
              </p>
              <p className="mt-0.5 text-xs text-neutral-500">
                pessoas vinculadas a setor
              </p>
            </div>
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
              <p className="flex items-center gap-1.5 text-xs font-medium uppercase text-sky-800">
                <ClipboardCheck className="h-3.5 w-3.5" />
                Conferentes
              </p>
              <p className="mt-1 text-2xl font-bold text-sky-900">
                {resumo.conferentes}
              </p>
              <p className="mt-0.5 text-xs text-sky-800/80">
                conferente base desta loja
              </p>
            </div>
          </div>

          <section className="space-y-4">
            <h2 className="text-sm font-semibold text-neutral-800">
              Usuários de produção por setor
            </h2>
            {relatorio.setores.map((setor) => (
              <article
                key={setor.setorId}
                className="overflow-hidden rounded-lg border border-neutral-200 bg-white"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-100 bg-neutral-50 px-4 py-3">
                  <div>
                    <p className="text-base font-semibold text-neutral-900">
                      {setor.nome}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {setor.usuarios.length}{" "}
                      {setor.usuarios.length === 1 ? "pessoa" : "pessoas"}
                      {setor.ativo ? "" : " · setor inativo"}
                    </p>
                  </div>
                  {!setor.ativo && (
                    <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-[11px] font-semibold text-neutral-600">
                      Inativo
                    </span>
                  )}
                </div>
                <TabelaUsuarios
                  usuarios={setor.usuarios}
                  empty="Nenhum usuário vinculado a este setor."
                />
              </article>
            ))}
            {relatorio.setores.length === 0 && (
              <div className="rounded-lg border border-neutral-200 bg-white px-3 py-6 text-center text-sm text-neutral-500">
                Esta loja ainda não tem setores cadastrados.
              </div>
            )}
          </section>

          <section>
            <h2 className="mb-3 text-sm font-semibold text-neutral-800">
              Conferentes da loja
            </h2>
            <div className="overflow-hidden rounded-lg border border-sky-200 bg-white">
              <TabelaUsuarios
                usuarios={relatorio.conferentes}
                empty="Nenhum conferente cadastrado nesta loja."
                mostrarConferente={false}
              />
            </div>
          </section>
        </div>
      )}
    </main>
  );
};

export default ControleProducaoRelatorioEquipePage;
